// src/stock/DetailProduit.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Button, Table, Spinner,
    Tooltip, OverlayTrigger, Pagination
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DetailProduit() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [produit, setProduit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mouvements, setMouvements] = useState([]);

    // Pagination des mouvements
    const [currentPageMvt, setCurrentPageMvt] = useState(1);
    const [itemsPerPageMvt] = useState(10);

    const isService = produit?.type_article === 'service';

    useEffect(() => {
        fetchProduit();
    }, [id]);

    // Ne charger les mouvements que si c'est un produit
    useEffect(() => {
        if (produit && produit.type_article !== 'service') {
            fetchMouvements();
        } else {
            setMouvements([]);
        }
    }, [produit]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchProduit = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setProduit(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchMouvements = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/mouvements_stock.php?produit=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setMouvements(result.data || []);
                setCurrentPageMvt(1);
            }
        } catch (err) {
            console.error("Erreur chargement mouvements", err);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getStockStatus = () => {
        if (!produit) return { label: '', gradient: '', color: '' };
        if (produit.type_article === 'service') {
            return {
                label: 'Service',
                gradient: 'linear-gradient(135deg, #21ACCD, #0ea5b7)',
                color: COLORS.secondary,
                icon: 'ri-service-line'
            };
        }
        if (!produit.stock_actuel || produit.stock_actuel <= 0) {
            return {
                label: 'Rupture',
                gradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: COLORS.danger,
                icon: 'ri-close-circle-line'
            };
        }
        if (produit.stock_actuel <= produit.seuil_alerte) {
            return {
                label: 'Critique',
                gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: COLORS.warning,
                icon: 'ri-alert-line'
            };
        }
        return {
            label: 'Normal',
            gradient: 'linear-gradient(135deg, #10b981, #059669)',
            color: COLORS.success,
            icon: 'ri-check-line'
        };
    };

    const getMouvementBadge = (type) => {
        if (type === 'entree') {
            return (
                <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                    <i className="ri-add-line"></i>Entrée
                </span>
            );
        }
        return (
            <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                <i className="ri-subtract-line"></i>Sortie
            </span>
        );
    };

    const getTypeArticleBadge = (type) => {
        if (type === 'service') {
            return (
                <span className="badge-type-service" title="Prestation non stockable">
                    <i className="ri-service-line"></i> Service
                </span>
            );
        }
        return (
            <span className="badge-type-produit" title="Article stockable">
                <i className="ri-box-3-line"></i> Produit
            </span>
        );
    };

    /* ============================================================
       PAGINATION MOUVEMENTS
       ============================================================ */
    const totalPagesMvt = Math.ceil(mouvements.length / itemsPerPageMvt);
    const startIndexMvt = (currentPageMvt - 1) * itemsPerPageMvt;
    const currentMouvements = mouvements.slice(startIndexMvt, startIndexMvt + itemsPerPageMvt);

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <Spinner
                                animation="border"
                                role="status"
                                className="mb-3"
                                style={{ width: '3rem', height: '3rem', color: COLORS.primary }}
                            >
                                <span className="visually-hidden">Chargement...</span>
                            </Spinner>
                            <h5 className="text-muted">Chargement de l'article...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !produit) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="empty-state-charte">
                        <div className="empty-state-icon-charte" style={{ background: 'rgba(239,68,68,0.1)', color: COLORS.danger }}>
                            <i className="ri-error-warning-line"></i>
                        </div>
                        <h6 className="fw-semibold mb-1">Article non trouvé</h6>
                        <p className="text-muted small mb-3">{error || "L'article que vous recherchez n'existe pas"}</p>
                        <Button className="btn-modern-outline" onClick={() => navigate('/stock/produits')}>
                            <i className="ri-arrow-left-line me-1"></i>Retour
                        </Button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const stockStatus = getStockStatus();

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className={isService ? "ri-service-line" : "ri-eye-line"}></i>
                            </div>
                            <div>
                                <h5 className="header-title">{produit.designation}</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-barcode-line"></i> {produit.reference}
                                    </span>
                                    {produit.nom_categorie && (
                                        <span className="badge-modern">
                                            <i className="ri-price-tag-3-line"></i> {produit.nom_categorie}
                                        </span>
                                    )}
                                    {getTypeArticleBadge(produit.type_article)}
                                    {!isService && (
                                        <span className="status-badge-charte" style={{ background: stockStatus.gradient }}>
                                            <i className={stockStatus.icon}></i>
                                            {stockStatus.label}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate('/stock/produits')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => navigate(`/stock/produits/modifier/${id}`)}>
                                <i className="ri-pencil-line"></i> Modifier
                            </Button>
                        </div>
                    </div>

                    <Row className="g-4">

                        {/* ===== COLONNE GAUCHE ===== */}
                        <Col lg={isService ? 12 : 8}>

                            {/* Informations générales */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Header className="card-header-gradient">
                                    <h5 className="mb-0 text-white d-flex align-items-center">
                                        <i className="ri-information-line me-2"></i>
                                        Informations générales
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <div className="section-title-charte">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-information-line"></i>
                                        </span>
                                        <h6>{isService ? 'Détails du service' : 'Détails du produit'}</h6>
                                    </div>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <div className="info-card-charte">
                                                <span className="info-label-charte">Référence</span>
                                                <div className="info-value-charte fw-semibold">{produit.reference}</div>
                                                {produit.code_barre && (
                                                    <small className="text-muted d-block mt-1">
                                                        <i className="ri-qr-code-line me-1"></i>
                                                        {produit.code_barre}
                                                    </small>
                                                )}
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-card-charte">
                                                <span className="info-label-charte">Désignation</span>
                                                <div className="info-value-charte fw-semibold">{produit.designation}</div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-card-charte">
                                                <span className="info-label-charte">Catégorie</span>
                                                <div className="info-value-charte fw-semibold">{produit.nom_categorie || 'Non catégorisé'}</div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-card-charte">
                                                <span className="info-label-charte">
                                                    {isService ? 'Unité de facturation' : 'Unité de mesure'}
                                                </span>
                                                <div className="info-value-charte fw-semibold">
                                                    {isService
                                                        ? (produit.unite_facturation || 'FORFAIT')
                                                        : (produit.unite_mesure || '—')}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={12}>
                                            <div className="info-card-charte">
                                                <span className="info-label-charte">Description</span>
                                                <p className="mb-0" style={{ fontSize: '0.9rem' }}>
                                                    {produit.description || 'Aucune description'}
                                                </p>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Prix et taxes */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Header className="card-header-gradient">
                                    <h5 className="mb-0 text-white d-flex align-items-center">
                                        <i className="ri-price-tag-3-line me-2"></i>
                                        {isService ? 'Montant et taxes' : 'Prix et taxes'}
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Row className="g-3 mb-3">
                                        {!isService && (
                                            <Col md={4}>
                                                <div className="price-box-charte">
                                                    <span className="info-label-charte">Prix d'achat HT</span>
                                                    <h4 className="price-value-charte text-muted">{formatCurrency(produit.prix_achat_ht)}</h4>
                                                </div>
                                            </Col>
                                        )}
                                        <Col md={isService ? 6 : 4}>
                                            <div className="price-box-charte highlighted">
                                                <span className="info-label-charte">
                                                    {isService ? 'Montant HT' : 'Prix de vente HT'}
                                                </span>
                                                <h4 className="price-value-charte" style={{ color: COLORS.primary }}>
                                                    {formatCurrency(produit.prix_vente_ht)}
                                                </h4>
                                            </div>
                                        </Col>
                                        {!isService && (
                                            <Col md={4}>
                                                <div className="price-box-charte">
                                                    <span className="info-label-charte">Marge</span>
                                                    <h4 className="price-value-charte" style={{ color: COLORS.success }}>
                                                        {formatCurrency(produit.prix_vente_ht - produit.prix_achat_ht)}
                                                    </h4>
                                                </div>
                                            </Col>
                                        )}
                                        {isService && (
                                            <Col md={6}>
                                                <div className="price-box-charte">
                                                    <span className="info-label-charte">Unité</span>
                                                    <h4 className="price-value-charte" style={{ color: COLORS.secondary }}>
                                                        {produit.unite_facturation || 'FORFAIT'}
                                                    </h4>
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                    <div className="section-title-charte">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                            <i className="ri-percent-line"></i>
                                        </span>
                                        <h6>Taxes</h6>
                                    </div>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <div className="info-card-charte">
                                                <span className="info-label-charte">TVA</span>
                                                <div className="info-value-charte fw-semibold">{produit.tva_taux}%</div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-card-charte">
                                                <span className="info-label-charte">CSS</span>
                                                <div className="info-value-charte fw-semibold">{produit.css_taux}%</div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* ===== COLONNE DROITE — STOCK (produits uniquement) ===== */}
                        {!isService && (
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white d-flex align-items-center">
                                            <i className="ri-stack-line me-2"></i>
                                            État du stock
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="text-center mb-4">
                                            <div className="stock-circle-charte" style={{ background: `${stockStatus.color}15` }}>
                                                <h1 className="stock-value-charte" style={{ color: stockStatus.color }}>
                                                    {formatNumber(produit.stock_actuel || 0)}
                                                </h1>
                                            </div>
                                            <span className="status-badge-charte" style={{ background: stockStatus.gradient }}>
                                                <i className={stockStatus.icon}></i>
                                                {stockStatus.label}
                                            </span>
                                        </div>

                                        <hr />

                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                <i className="ri-settings-3-line"></i>
                                            </span>
                                            <h6>Paramètres</h6>
                                        </div>

                                        <Row className="g-3">
                                            <Col xs={6}>
                                                <div className="stock-info-box-charte">
                                                    <span className="info-label-charte">Seuil d'alerte</span>
                                                    <div className="info-value-charte">{produit.seuil_alerte}</div>
                                                </div>
                                            </Col>
                                            <Col xs={6}>
                                                <div className="stock-info-box-charte">
                                                    <span className="info-label-charte">Stock minimum</span>
                                                    <div className="info-value-charte">{produit.stock_minimum}</div>
                                                </div>
                                            </Col>
                                            <Col xs={6}>
                                                <div className="stock-info-box-charte">
                                                    <span className="info-label-charte">Stock maximum</span>
                                                    <div className="info-value-charte">{produit.stock_maximum || '—'}</div>
                                                </div>
                                            </Col>
                                            <Col xs={6}>
                                                <div className="stock-info-box-charte">
                                                    <span className="info-label-charte">Emplacement</span>
                                                    <div className="info-value-charte">{produit.emplacement || '—'}</div>
                                                </div>
                                            </Col>
                                        </Row>

                                        <div className="d-grid gap-2 mt-4">
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Ajouter du stock</Tooltip>}>
                                                <Button
                                                    className="btn-modern-success justify-content-center"
                                                    onClick={() => navigate(`/stock/mouvements/ajouter/${id}?type=entree`)}
                                                >
                                                    <i className="ri-add-box-line me-2"></i>
                                                    Ajouter au stock
                                                </Button>
                                            </OverlayTrigger>
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Retirer du stock</Tooltip>}>
                                                <Button
                                                    className="btn-modern-warning justify-content-center"
                                                    onClick={() => navigate(`/stock/mouvements/ajouter/${id}?type=sortie`)}
                                                    disabled={produit.stock_actuel <= 0}
                                                >
                                                    <i className="ri-subtract-line me-2"></i>
                                                    Retirer du stock
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}

                        {/* ===== BLOC INFO SERVICE (remplace le stock) ===== */}
                        {isService && (
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white d-flex align-items-center">
                                            <i className="ri-service-line me-2"></i>
                                            Nature du service
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="text-center mb-4">
                                            <div className="service-circle-charte">
                                                <i className="ri-service-line"></i>
                                            </div>
                                            <span className="status-service-charte mt-3">
                                                <i className="ri-service-line"></i> Prestation
                                            </span>
                                        </div>

                                        <hr />

                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                <i className="ri-information-line"></i>
                                            </span>
                                            <h6>Caractéristiques</h6>
                                        </div>

                                        <div className="service-info-box">
                                            <i className="ri-information-line"></i>
                                            <div>
                                                <strong>Service non quantifiable</strong>
                                                <p className="mb-0 small text-muted">
                                                    Ce service n'est pas soumis à la gestion de stock.
                                                    Il est facturé au montant HT défini ci-dessus, avec la TVA
                                                    et le CSS appliqués automatiquement. L'unité de facturation
                                                    précise le mode de tarification (forfait, jour, heure…).
                                                </p>
                                            </div>
                                        </div>

                                        <div className="d-grid gap-2 mt-4">
                                            <Button
                                                className="btn-modern-primary justify-content-center"
                                                onClick={() => navigate('/ventes/factures/nouveau')}
                                            >
                                                <i className="ri-file-list-3-line me-2"></i>
                                                Facturer ce service
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}

                        {/* ===== HISTORIQUE DES MOUVEMENTS (produits uniquement) ===== */}
                        {!isService && (
                            <Col lg={12}>
                                <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                                    <Card.Header className="card-header-gradient">
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <i className="ri-history-line text-white fs-5"></i>
                                                <span className="fw-semibold text-white">Historique des mouvements</span>
                                            </div>
                                            {mouvements.length > 0 && (
                                                <span className="badge-header-light">{mouvements.length} mouvement(s)</span>
                                            )}
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <div className="table-responsive">
                                            <Table hover className="align-middle mb-0 table-charte">
                                                <thead>
                                                    <tr>
                                                        <th className="ps-4">Date</th>
                                                        <th className="text-center">Type</th>
                                                        <th className="text-center">Quantité</th>
                                                        <th className="text-center">Stock avant</th>
                                                        <th className="text-center">Stock après</th>
                                                        <th>Document</th>
                                                        <th className="pe-4">Commentaire</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentMouvements.length > 0 ? (
                                                        currentMouvements.map((mvt, idx) => (
                                                            <tr key={idx}>
                                                                <td className="ps-4 text-muted">
                                                                    <i className="ri-calendar-line me-1"></i>
                                                                    {formatDate(mvt.date_mouvement)}
                                                                </td>
                                                                <td className="text-center">{getMouvementBadge(mvt.type_mouvement)}</td>
                                                                <td className="text-center fw-bold">
                                                                    <span style={{ color: mvt.type_mouvement === 'entree' ? COLORS.success : COLORS.danger }}>
                                                                        {mvt.type_mouvement === 'entree' ? '+' : '−'}{mvt.quantite}
                                                                    </span>
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="quantity-badge-charte">{mvt.stock_avant}</span>
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="quantity-badge-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                                        {mvt.stock_apres}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {mvt.reference_document ? (
                                                                        <>
                                                                            <small className="fw-semibold">{mvt.reference_document}</small>
                                                                            {mvt.type_document && (
                                                                                <div><small className="text-muted">{mvt.type_document}</small></div>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <span className="text-muted">—</span>
                                                                    )}
                                                                </td>
                                                                <td className="pe-4">
                                                                    <small className="text-muted">{mvt.commentaire || '—'}</small>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="7">
                                                                <div className="empty-state-charte">
                                                                    <div className="empty-state-icon-charte">
                                                                        <i className="ri-history-line"></i>
                                                                    </div>
                                                                    <h6 className="fw-semibold mb-1">Aucun mouvement enregistré</h6>
                                                                    <p className="text-muted small mb-3">
                                                                        Commencez par ajouter ou retirer du stock
                                                                    </p>
                                                                    <Button
                                                                        className="btn-modern-primary"
                                                                        onClick={() => navigate(`/stock/mouvements/ajouter/${id}?type=entree`)}
                                                                    >
                                                                        <i className="ri-add-box-line me-2"></i>
                                                                        Premier mouvement
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>

                                    {totalPagesMvt > 1 && (
                                        <Card.Footer className="bg-white border-top py-3 px-4">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                                <div className="pagination-info-charte">
                                                    Affichage de <strong>{startIndexMvt + 1}</strong> à <strong>{Math.min(startIndexMvt + itemsPerPageMvt, mouvements.length)}</strong> sur <strong>{mouvements.length}</strong> mouvement(s)
                                                </div>
                                                <Pagination className="pagination-charte mb-0">
                                                    <Pagination.Prev
                                                        onClick={() => setCurrentPageMvt(p => Math.max(1, p - 1))}
                                                        disabled={currentPageMvt === 1}
                                                    />
                                                    {[...Array(Math.min(5, totalPagesMvt))].map((_, i) => {
                                                        let pageNum;
                                                        if (totalPagesMvt <= 5) pageNum = i + 1;
                                                        else if (currentPageMvt <= 3) pageNum = i + 1;
                                                        else if (currentPageMvt >= totalPagesMvt - 2) pageNum = totalPagesMvt - 4 + i;
                                                        else pageNum = currentPageMvt - 2 + i;
                                                        return (
                                                            <Pagination.Item
                                                                key={i}
                                                                active={pageNum === currentPageMvt}
                                                                onClick={() => setCurrentPageMvt(pageNum)}
                                                            >
                                                                {pageNum}
                                                            </Pagination.Item>
                                                        );
                                                    })}
                                                    <Pagination.Next
                                                        onClick={() => setCurrentPageMvt(p => Math.min(totalPagesMvt, p + 1))}
                                                        disabled={currentPageMvt === totalPagesMvt}
                                                    />
                                                </Pagination>
                                            </div>
                                        </Card.Footer>
                                    )}
                                </Card>
                            </Col>
                        )}
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== BADGES DE TYPE ===== */
                .badge-type-service {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: linear-gradient(135deg, #f0fbff, #e8f7fc);
                    color: var(--secondary);
                    padding: 0.25rem 0.7rem;
                    border-radius: 50px;
                    font-size: 0.68rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    border: 1px solid rgba(33, 172, 205, 0.3);
                    white-space: nowrap;
                }
                .badge-type-produit {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9;
                    color: #64748b;
                    padding: 0.25rem 0.7rem;
                    border-radius: 50px;
                    font-size: 0.68rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    border: 1px solid #e2e8f0;
                    white-space: nowrap;
                }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== INFO CARD ===== */
                .info-card-charte {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 1rem 1.25rem;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                }
                .info-card-charte:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                    border-color: var(--secondary);
                }
                .info-label-charte {
                    display: block;
                    color: #64748b;
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    font-weight: 600;
                    margin-bottom: 4px;
                }
                .info-value-charte {
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 1rem;
                }

                /* ===== PRICE BOX ===== */
                .price-box-charte {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 1.25rem;
                    text-align: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    height: 100%;
                }
                .price-box-charte.highlighted {
                    background: ${COLORS.primary}08;
                    border-color: ${COLORS.primary}33;
                }
                .price-box-charte:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }
                .price-value-charte {
                    font-size: 1.35rem;
                    font-weight: 700;
                    margin: 0.25rem 0 0 0;
                }

                /* ===== STOCK CIRCLE ===== */
                .stock-circle-charte {
                    width: 120px;
                    height: 120px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                }
                .stock-value-charte {
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin: 0;
                }

                /* ===== SERVICE CIRCLE ===== */
                .service-circle-charte {
                    width: 120px;
                    height: 120px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #f0fbff, #e8f7fc);
                    border: 3px dashed rgba(33, 172, 205, 0.4);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                }
                .service-circle-charte i {
                    font-size: 3rem;
                    color: var(--secondary);
                }
                .status-service-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: linear-gradient(135deg, #e0f2fe, #bae6fd);
                    color: #0369a1;
                    padding: 0.35rem 0.9rem;
                    border-radius: 50px;
                    font-size: 0.75rem;
                    font-weight: 700;
                    border: 1px solid rgba(3, 105, 161, 0.25);
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                /* ===== STOCK INFO BOX ===== */
                .stock-info-box-charte {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 1rem;
                    text-align: center;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                }
                .stock-info-box-charte:hover {
                    border-color: var(--secondary);
                }
                .stock-info-box-charte .info-value-charte {
                    font-size: 1.1rem;
                }

                /* ===== SERVICE INFO BOX ===== */
                .service-info-box {
                    display: flex; gap: .9rem; align-items: flex-start;
                    background: linear-gradient(135deg, #f0fbff, #e8f7fc);
                    border-left: 4px solid var(--secondary);
                    padding: 1.1rem 1.3rem; border-radius: 12px;
                }
                .service-info-box i { color: var(--secondary); font-size: 1.4rem; margin-top: 2px; }

                /* ===== STATUS BADGE ===== */
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.35rem 0.85rem;
                    border-radius: 50px; font-size: 0.75rem; font-weight: 600;
                    white-space: nowrap;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .quantity-badge-charte {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 50px;
                    padding: 0.25rem 0.5rem;
                    background: #f1f5f9;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.85rem;
                    color: #1e293b;
                }

                /* ===== PAGINATION ===== */
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled,
                .btn-modern-success:disabled,
                .btn-modern-warning:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .stock-circle-charte { width: 100px; height: 100px; }
                    .stock-value-charte { font-size: 2rem; }
                    .service-circle-charte { width: 100px; height: 100px; }
                    .service-circle-charte i { font-size: 2.5rem; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}